import { defineTour } from '../../types';
import { PERMISSIONS, canAll, canAny } from './access';

/**
 * T09 — Tenant administration and readiness (brief §7).
 *
 * For a seat that may read setup, users, roles or approval rules — the
 * administration screens. Budgets alone do not make a seat an administrator: a
 * branch requester and an approver may read budgets (SystemRole), and are not
 * offered an administration guide for it. Each step is shown only with the
 * permission its menu entry needs (and the import card with `import.view`, as
 * the setup page gates it). Every step's page is a list or a read-first page
 * reached by GET only. The organisation profile and the settings are edit
 * forms saved on submit (safeRoutes: EXCLUDED), so the guide does not take
 * anybody there. No file is ever chosen, checked or applied by the guide.
 *
 * Copy: `guidance.tours.T09.*` — draft, for native-language review.
 */
export const T09 = defineTour({
  id: 'T09',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T09.title',
  summaryKey: 'guidance.tours.T09.summary',
  eligible: (ctx) =>
    ctx.portal === 'tenant' &&
    canAny(
      ctx,
      PERMISSIONS.onboardingView,
      PERMISSIONS.userView,
      PERMISSIONS.roleView,
      PERMISSIONS.approvalRuleView,
    ),
  pageRoutes: [
    'tenant.onboarding',
    'tenant.users',
    'tenant.roles',
    'tenant.approval-rules',
    'tenant.budgets',
  ],
  steps: [
    {
      id: 't09-readiness',
      anchor: 'tenant.setup.readiness',
      route: 'tenant.onboarding',
      when: (ctx) => ctx.can(PERMISSIONS.onboardingView),
      titleKey: 'guidance.tours.T09.readiness.title',
      bodyKey: 'guidance.tours.T09.readiness.body',
    },
    {
      id: 't09-import',
      anchor: 'tenant.setup.import',
      route: 'tenant.onboarding',
      when: (ctx) => canAll(ctx, PERMISSIONS.onboardingView, PERMISSIONS.importView),
      titleKey: 'guidance.tours.T09.import.title',
      bodyKey: 'guidance.tours.T09.import.body',
    },
    {
      id: 't09-import-history',
      anchor: 'tenant.setup.history',
      route: 'tenant.onboarding',
      when: (ctx) => canAll(ctx, PERMISSIONS.onboardingView, PERMISSIONS.importView),
      titleKey: 'guidance.tours.T09.importHistory.title',
      bodyKey: 'guidance.tours.T09.importHistory.body',
    },
    {
      id: 't09-users',
      anchor: 'tenant.admin.users-list',
      route: 'tenant.users',
      when: (ctx) => ctx.can(PERMISSIONS.userView),
      titleKey: 'guidance.tours.T09.users.title',
      bodyKey: 'guidance.tours.T09.users.body',
    },
    {
      id: 't09-roles',
      anchor: 'tenant.admin.roles-list',
      route: 'tenant.roles',
      when: (ctx) => ctx.can(PERMISSIONS.roleView),
      titleKey: 'guidance.tours.T09.roles.title',
      bodyKey: 'guidance.tours.T09.roles.body',
    },
    {
      id: 't09-rules',
      anchor: 'tenant.rules.list',
      route: 'tenant.approval-rules',
      when: (ctx) => ctx.can(PERMISSIONS.approvalRuleView),
      titleKey: 'guidance.tours.T09.rules.title',
      bodyKey: 'guidance.tours.T09.rules.body',
    },
    {
      id: 't09-budgets',
      anchor: 'tenant.budgets.list',
      route: 'tenant.budgets',
      when: (ctx) => ctx.can(PERMISSIONS.budgetView),
      titleKey: 'guidance.tours.T09.budgets.title',
      bodyKey: 'guidance.tours.T09.budgets.body',
    },
  ],
});
